/**
 * W66.W6 — Relationship and entry path section.
 * Two paragraph blocks side-by-side on desktop, stacked on mobile.
 * Left: Bryan's relationships. Right: Entry path strategy.
 * Below: Threads chips row.
 */

import React from "react";

interface RelationshipBlockProps {
  planData: Record<string, any>;
}

export default function RelationshipBlock({ planData }: RelationshipBlockProps) {
  const rel = planData.relationship ?? {};
  const ep = planData.entry_path ?? {};

  const relNarrative = rel.narrative ?? "";
  const primaryContact = rel.primary_contact_name ?? "";
  const backups: string[] = rel.backup_contact_names ?? [];
  const warmIntro = rel.warm_intro_path ?? "";

  const threadA = ep.thread_a ?? "";
  const threadB = ep.thread_b ?? "";
  const escalation = ep.escalation_trigger ?? "";

  return (
    <section style={{ marginBottom: 28 }}>
      <h2
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#374151",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          margin: "0 0 10px",
          borderBottom: "1px solid #E5E7EB",
          paddingBottom: 6,
        }}
      >
        Relationship &amp; Entry Path
      </h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          marginBottom: 16,
        }}
      >
        {/* Bryan's relationships */}
        <div
          style={{
            background: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            padding: 16,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 8,
            }}
          >
            Bryan's Relationships
          </div>
          <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: 0 }}>
            {relNarrative || "\u2014"}
          </p>
        </div>

        {/* Entry path strategy */}
        <div
          style={{
            background: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            padding: 16,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#6B7280",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 8,
            }}
          >
            Entry Path Strategy
          </div>
          {threadA && (
            <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: "0 0 6px" }}>
              <strong>Thread A:</strong> {threadA}
            </p>
          )}
          {threadB && (
            <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, margin: "0 0 6px" }}>
              <strong>Thread B:</strong> {threadB}
            </p>
          )}
          {escalation && (
            <p style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.5, margin: 0, fontStyle: "italic" }}>
              {escalation}
            </p>
          )}
        </div>
      </div>

      {/* Threads chips row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#6B7280" }}>Threads:</span>

        {primaryContact && (
          <span
            style={{
              background: "#EFF6FF",
              border: "1px solid #BFDBFE",
              color: "#0B0B0F",
              borderRadius: 20,
              padding: "3px 10px",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Primary: {primaryContact}
          </span>
        )}

        {backups.map((b, i) => (
          <span
            key={i}
            style={{
              background: "#F9FAFB",
              border: "1px solid #E5E7EB",
              color: "#374151",
              borderRadius: 20,
              padding: "3px 10px",
              fontSize: 12,
            }}
          >
            Backup: {b}
          </span>
        ))}

        {warmIntro && (
          <span
            style={{
              background: "#FFF7ED",
              border: "1px solid #FED7AA",
              color: "#EA580C",
              borderRadius: 20,
              padding: "3px 10px",
              fontSize: 12,
            }}
          >
            Warm intro: {warmIntro}
          </span>
        )}
      </div>
    </section>
  );
}
